import Vue from 'vue';
import Vuex from 'vuex';
import isTokenValid from '@/utils/token';
import config from '@/config';

Vue.use(Vuex);

const STATE_VAL_JSON = 'STATE_VAL_JSON';
const STATE_VAL_STRING = 'STATE_VAL_STRING';

const setStateFactory = (prop, { type = STATE_VAL_STRING, fn }) => (state, payload) => {
  state[prop] = payload;
  localStorage.setItem(prop, type === STATE_VAL_JSON ? JSON.stringify(payload) : payload);

  if (fn) { fn(state, payload); }
};

const storeSignOut = () => {
  localStorage.removeItem('jwtacc');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.removeItem('supplier');

  delete Vue.prototype.$http.defaults.headers.jwtacc;
};

export default new Vuex.Store({
  state: {
    workMode: config.workMode,
    user: JSON.parse(localStorage.getItem('user')),
    supplier: JSON.parse(localStorage.getItem('supplier')),
    realm: localStorage.getItem('realm') || '',
    authLocation: JSON.parse(localStorage.getItem('auth-location')) || null,
  },
  mutations: {
    setRealm: setStateFactory('realm', { type: STATE_VAL_STRING }),
    setUser: setStateFactory('user', { type: STATE_VAL_JSON }),
    setSupplier: setStateFactory('supplier', { type: STATE_VAL_JSON }),

    signOut: (state) => {
      Vue.prototype.$log.debug('store signOut');
      state.user = null;
      state.supplier = null;

      storeSignOut();
    },

    setToken: (state, jwt, refreshToken) => {
      localStorage.setItem('jwtacc', jwt);
      Vue.prototype.$http.defaults.headers.jwtacc = jwt;

      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }
    },
    setAuthRedirectLocation(state, payload) {
      Vue.prototype.$log.debug('store setAuthRedirectLocation', payload);

      state.authLocation = payload;
      localStorage.setItem('auth-location', JSON.stringify(payload));
    },

    clearAuthRedirectLocation(state, payload) {
      Vue.prototype.$log.debug('store clearAuthRedirectLocation', payload);

      state.authLocation = null;
      localStorage.removeItem('auth-location');
    },
  },
  getters: {
    full: (state) => state.user && (state.user.interfaces || []).includes('Full'),
    aurus: (state) => state.user && (state.user.interfaces || []).includes('Aurus'),
    business: (state) => state.user && (
      (state.user.interfaces || []).includes('Business')
      || (state.user.interfaces || []).includes('Aurus')
    ),
    supplier: (state) => state.user && (state.user.interfaces || []).includes('Supplier'),
    security: (state) => state.user && (state.user.interfaces || []).includes('Security'),
    editor: (state) => state.user && (state.user.interfaces || []).includes('Editor'),
    read: (state) => state.user && (state.user.interfaces || []).includes('Read'),
    consolidator: (state) => state.user && (state.user.interfaces || []).includes('Consolidator'),
    user: (state) => state.user,
    authLocation: (state) => state.authLocation,
  },
  actions: {
    ssoSignOut: async ({ commit, state }) => {
      const { $keycloak, $log } = Vue.prototype;
      $log.debug('SSO Sign out');

      commit('signOut');

      // Keycloak Logout will redirect user, so it should be last in action
      if (state.realm
        && $keycloak
        && $keycloak.adapters[state.realm]
        && $keycloak.adapters[state.realm].realm) {
        await $keycloak.adapters[state.realm].logout();
      }
    },

    refreshBackendToken: async ({ commit }, payload) => {
      const currentRefreshToken = localStorage.getItem('refreshToken');
      if (!currentRefreshToken || !isTokenValid(currentRefreshToken)) {
        return Promise.reject(payload);
      }

      const { $http, $log } = Vue.prototype;
      try {
        // We will keep jwtacc in defaults, but try refresh token with empty jwtacc header
        const { data } = await $http.$token.post('main/token-validate', {
          token: payload,
        });

        const { jwtacc, refreshToken } = data;
        return commit('setToken', jwtacc, refreshToken);
      } catch (error) {
        $log.debug('Error Get backend token', error);
        return Promise.reject(payload);
      }
    },

    refreshKeycloakToken: async ({ commit, state }, payload) => {
      const { $keycloak, $log } = Vue.prototype;

      const adapter = $keycloak.adapters[state.realm];
      try {
        await adapter.updateToken();
        return commit('setToken', adapter.token);
      } catch (error) {
        $log.debug('Error refresh keycloak token', error);
        return Promise.reject(payload);
      }
    },

    checkVersion: async () => {
      const { $http, $sentry } = Vue.prototype;

      const { data } = await $http(`../version.json?${Date.now()}`);
      const { version } = data;

      try {
        await $http(`versions/front/front?version=${version}`);
      } catch (error) {
        $sentry?.captureException(error);
      }

      $sentry?.setTag('version', version);
    },

    getSelf: async ({ commit }, payload) => {
      const { $http, $log } = Vue.prototype;
      const { jwtacc } = payload || $http.defaults.headers || {};

      const reqHeaders = { headers: { jwtacc } };
      const { data: user } = await $http.$token.get('main/getself', reqHeaders);
      $log.debug('userData', user);

      commit('setUser', user);
    },

    loadSupplierData: async ({ commit, state }) => {
      const { $http, $moment } = Vue.prototype;
      const { user } = state;

      if (user.asdb) {
        const params = { asdb: user.asdb };

        const { data: supplierData } = await $http.get('/suppliers-get-by-asdb', { params });
        commit('setSupplier', supplierData);

        if (supplierData) {
          $moment.tz.setDefault(supplierData.timezone);
        } else {
          $moment.tz.setDefault();
        }
      }
    },

    performAuth: async ({ dispatch, commit, state }, payload) => {
      const {
        jwtacc, refreshToken, locale, app,
      } = payload;

      const {
        $http, $log, $sentry,
      } = app;

      $log.debug('performAuth');

      try {
        commit('setToken', jwtacc);
        await dispatch('getSelf');

        const { user } = state;

        $log.debug('userData', user);

        await dispatch('loadSupplierData');

        if (locale && locale !== user.language) {
          await $http.put(`users/${user.id}`, { language: locale });

          user.language = locale;
        }

        $sentry?.configureScope((scope) => {
          scope.setTag('dealer', user.asdb);
          scope.setUser({
            username: user.login, userId: user.id, code: user.asdb, wslId: user.wslId,
          });
        });

        if (refreshToken) {
          localStorage.setItem('refreshToken', refreshToken);
        }
      } catch (e) {
        $log.debug('getSelfError', e.message);

        const { data = {}, type } = e.response || {};

        $log.debug('Perform auth handle-error', type, data, e.response);
        throw e;
      }
    },
  },
});
